// 3.3 The Contact finders card (job F-03; docs/specs/UPGRADES_2026_10.md "F-03 Contact finders card"). Reads
// crm_contact_finder_stats (migration 20261223030000) through crm.read, and proposes the find_contact_finders settings
// section through crm.write (act propose_settings_change; the owner approves it at 0.5). The types, the plain-English
// lines and the order rules live here so the card, its demo and its tests share them. Never add methods to lib/crm.ts.
//
// Bug wave 20 Oct 2026: the order the finders are asked in is Ogma's own method (#358; engine/contact_providers
// OGMA_ORDER), so the business chooses only which finders are used; and the card no longer sets a monthly cap (#357):
// paid finders stop at Find's contact finders spending cap in Rules › Approvals and spend.
import type { Crm } from "./crm";
import type { Integration } from "./integrations";
import type { ChecklistItem, JsonObject } from "./types";
import { EMAIL_FINDERS } from "./settingsRules";
import { money } from "./approvalsFind";

export type FinderKey = (typeof EMAIL_FINDERS)[number];
export type FinderKind = "free" | "verifier" | "paid" | "search";

export interface FinderStats {
  key: FinderKey;
  kind: FinderKind;
  in_order: boolean;
  position: number | null;
  account_status?: string;
  /** Last 30 days. For Hunter, its searches; found is null (MillionVerifier checks what it finds). */
  attempts?: number | null;
  found?: number | null;
  verified?: number | null;
  catchall?: number | null;
  misses?: number | null;
  timeouts?: number | null;
  errors?: number | null;
  hit_rate?: number | null;
  cost_usd?: number | null;
  cost_per_found_usd?: number | null;
  /** This month, against the finder's own cap (budget.vendors.<vendor>, E14). */
  month_spend_usd?: number | null;
  cap_month_usd?: number | null;
  cap_day_usd?: number | null;
  cost_per_call_usd?: number | null;
}

export interface VerifierStats {
  checks: number;
  verified: number;
  catchall: number;
  cost_usd: number;
  cost_per_found_usd: number | null;
}

export interface ContactFinderStats {
  as_of: string | null;
  since: string | null;
  month_start: string | null;
  /** The finders used (tools.email_finding), in Ogma's order as the engine runs them: own first. */
  order: FinderKey[];
  finders: FinderStats[];
  verifier: VerifierStats;
  /** Find's contact finders spending cap this month, in the business's currency (#357, #384); null when not read. */
  spend_cap: { cap: number; spent: number; over: boolean; currency: string } | null;
  month_spend_usd: number;
  phone_finding: boolean;
  first_business: boolean;
  provider_data_allowed: boolean;
  provider_data_reviewed_at: string | null;
}

export const FINDER_LABELS: Record<FinderKey, string> = {
  own: "Your own records",
  site: "Their website",
  pattern: "Pattern guesses",
  bettercontact: "BetterContact",
  fullenrich: "FullEnrich",
  hunter: "Hunter",
};

export const FINDER_WHAT: Record<FinderKey, string> = {
  own: "People and addresses Ogma already holds. Free, and always first.",
  site: "An address on the firm's own website for this person, checked with MillionVerifier.",
  pattern: "Likely addresses (jo.smith@, jsmith@ …), most common first, each checked with MillionVerifier.",
  bettercontact: "Asks many data sources at once and checks catch-all addresses. You pay only for emails found.",
  fullenrich: "Many data sources, pay only for emails found. The alternative to BetterContact.",
  hunter: "Hunter's email finder. Each answer is then checked with MillionVerifier.",
};

/** Finders that use provider-sourced contact data: locked by the lawyer gate (migration 20261221050200). */
export const GATED: readonly FinderKey[] = ["bettercontact", "fullenrich", "hunter"];
export const PAID: readonly FinderKey[] = ["bettercontact", "fullenrich"];
/** Finders whose key is pasted on this card. */
export const KEYED: readonly FinderKey[] = ["bettercontact", "fullenrich", "hunter"];

/** Hunter is not one of the Oct 2026 integrations (lib/integrations.ts), so the card describes it itself. */
export const HUNTER_INTEGRATION: Integration = {
  key: "hunter", label: "Hunter", vendor: "hunter", kind: "paste",
  secrets: [{ name: "api_key", label: "API key" }], cost: "About $0.10 a search (Ogma's estimate; Hunter's free plan has 25 a month)",
  signupUrl: "https://hunter.io", steps: ["3.3"], page: "/settings/data/sources", module: "find",
  whatItDoes: "Finds a person's email from their name and the firm's domain.",
};

export const LOCK_TITLE = "Paid finders and phone finding are locked";
export const LOCK_TEXT =
  "Contact details bought from data providers wait until a lawyer has reviewed your privacy notice wording (the Article 14 notice). Your own records, the website and pattern guesses keep working. When the review is done, its date goes into your playbook and this unlocks.";

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null;
const isKey = (v: unknown): v is FinderKey => typeof v === "string" && (EMAIL_FINDERS as readonly string[]).includes(v);

/** Own first, known names once each, in the order given. */
export function normaliseOrder(list: readonly unknown[]): FinderKey[] {
  const out: FinderKey[] = ["own"];
  for (const v of list) {
    const k = typeof v === "string" ? v.trim().toLowerCase() : v;
    if (isKey(k) && !out.includes(k)) out.push(k);
  }
  return out;
}

/** The engine's order rule (engine/contact_providers.finder_order, #358): the finders named, always in Ogma's order
 * (EMAIL_FINDERS: own, site, pattern, BetterContact, FullEnrich, Hunter), whatever order the list is in. */
export function ogmaOrder(list: readonly unknown[]): FinderKey[] {
  const named = normaliseOrder(list);
  return EMAIL_FINDERS.filter((k) => named.includes(k));
}

function spendCap(v: unknown): ContactFinderStats["spend_cap"] {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  const cap = num(o.cap);
  if (cap === null) return null;
  const spent = num(o.spent) ?? 0;
  return { cap, spent, over: o.over === true || spent >= cap, currency: str(o.currency) ?? "GBP" };
}

/** Find's contact finders cap, once this month's spend reaches it (#414), else null. */
export function capReachedLine(s: Pick<ContactFinderStats, "spend_cap">): string | null {
  const c = s.spend_cap;
  if (!c || c.cap <= 0 || !c.over) return null;
  return `Find's contact finders spending cap of ${money(c.cap, c.currency)} is reached this month, so paid finders wait until next month.`;
}

/** Reads crm_contact_finder_stats tolerantly, so a missing field never breaks the page. */
export function readStats(raw: unknown): ContactFinderStats {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rows = Array.isArray(o.finders) ? (o.finders as Record<string, unknown>[]) : [];
  const finders: FinderStats[] = EMAIL_FINDERS.map((key) => {
    const r = rows.find((x) => x && x.key === key) ?? {};
    const n = (f: string) => num(r[f]);
    return {
      key,
      kind: (str(r.kind) as FinderKind | null) ?? (PAID.includes(key) ? "paid" : key === "hunter" ? "search" : key === "own" ? "free" : "verifier"),
      in_order: r.in_order === true,
      position: n("position"),
      account_status: str(r.account_status) ?? undefined,
      attempts: n("attempts"), found: n("found"), verified: n("verified"), catchall: n("catchall"), misses: n("misses"),
      timeouts: n("timeouts"), errors: n("errors"), hit_rate: n("hit_rate"), cost_usd: n("cost_usd"),
      cost_per_found_usd: n("cost_per_found_usd"), month_spend_usd: n("month_spend_usd"), cap_month_usd: n("cap_month_usd"),
      cap_day_usd: n("cap_day_usd"), cost_per_call_usd: n("cost_per_call_usd"),
    };
  });
  const v = (o.verifier && typeof o.verifier === "object" ? o.verifier : {}) as Record<string, unknown>;
  const order = Array.isArray(o.order) ? normaliseOrder(o.order) : normaliseOrder(finders.filter((f) => f.in_order).sort((a, b) => (a.position ?? 99) - (b.position ?? 99)).map((f) => f.key));
  return {
    as_of: str(o.as_of), since: str(o.since), month_start: str(o.month_start),
    order,
    finders,
    verifier: {
      checks: num(v.checks) ?? 0, verified: num(v.verified) ?? 0, catchall: num(v.catchall) ?? 0, cost_usd: num(v.cost_usd) ?? 0,
      cost_per_found_usd: num(v.cost_per_found_usd),
    },
    spend_cap: spendCap(o.spend_cap),
    month_spend_usd: num(o.month_spend_usd) ?? 0,
    phone_finding: o.phone_finding === true,
    first_business: o.first_business === true,
    provider_data_allowed: o.provider_data_allowed === true,
    provider_data_reviewed_at: str(o.provider_data_reviewed_at),
  };
}

/** 3.3 the card's read. */
export async function contactFinderStats(crm: Crm): Promise<ContactFinderStats> {
  return readStats(await crm.read<unknown>("crm_contact_finder_stats", {}));
}

// ------------------------------------------------------------------ words
export function usd(v: number | null | undefined): string {
  const n = v ?? 0;
  if (n === 0) return "$0";
  if (n < 0.1) return `$${n.toFixed(3).replace(/0$/, "")}`;
  if (n < 1) return `$${n.toFixed(2)}`;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

export function pct(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? "" : `${Math.round(rate * 100)}%`;
}

export function isLocked(s: ContactFinderStats, key: FinderKey): boolean {
  return !s.provider_data_allowed && GATED.includes(key);
}

/** Connected (a key saved or checked) for a keyed finder. */
export function connectedStatus(status: string | undefined): boolean {
  return status === "verified" || status === "credentials_added";
}

/** One line of 30-day results for a finder row. */
export function statsLine(s: ContactFinderStats, f: FinderStats): string {
  switch (f.key) {
    case "own":
      return "Free · always first";
    case "site":
    case "pattern": {
      // MillionVerifier runs on Ogma's central key (decision 016, #422): there is never a key for the owner to add.
      const v = s.verifier;
      if (v.checks === 0) return "No checks in the last 30 days";
      const per = v.cost_per_found_usd !== null ? ` · ${usd(v.cost_per_found_usd)} per verified email` : "";
      return `MillionVerifier: ${v.verified} verified, ${v.catchall} catch-all from ${v.checks} checks${per} · 30 days`;
    }
    case "hunter":
      if (!f.attempts) return "No searches in the last 30 days";
      return `${f.attempts} ${f.attempts === 1 ? "search" : "searches"}, ${usd(f.cost_usd)} · 30 days`;
    default: {
      const tried = (f.attempts ?? 0) - (f.errors ?? 0);
      if (!f.attempts) return "Not used in the last 30 days";
      const found = f.found ?? 0;
      const per = f.cost_per_found_usd !== null && f.cost_per_found_usd !== undefined ? ` · ${usd(f.cost_per_found_usd)} per email found` : " · nothing paid";
      const ca = f.catchall ? ` (${f.catchall} catch-all)` : "";
      return `Found ${found} of ${tried} (${pct(f.hit_rate ?? (tried ? found / tried : 0))})${ca}${per} · 30 days`;
    }
  }
}

/** The finder's status word: what stops it, if anything. */
export function statusWord(s: ContactFinderStats, f: FinderStats): string {
  if (isLocked(s, f.key)) return "Locked";
  if (f.key === "own") return "On";
  if (f.key === "site" || f.key === "pattern") return "On";
  if (!connectedStatus(f.account_status)) return "Not connected";
  if (f.cap_month_usd !== null && f.cap_month_usd !== undefined && (f.month_spend_usd ?? 0) >= f.cap_month_usd) return "Cap reached";
  return "Connected";
}

/** The card's subtitle: the state in a few words. */
export function cardLine(s: ContactFinderStats): string {
  const steps = s.order.length;
  const paid = s.finders.filter((f) => PAID.includes(f.key));
  const best = paid.filter((f) => s.order.includes(f.key) && (f.attempts ?? 0) > 0).sort((a, b) => (b.hit_rate ?? 0) - (a.hit_rate ?? 0))[0];
  const parts = [`${steps} ${steps === 1 ? "finder" : "finders"} in use`];
  if (!s.provider_data_allowed) parts.push("paid finders locked");
  else if (best) parts.push(`${FINDER_LABELS[best.key]} finds ${pct(best.hit_rate)}${best.cost_per_found_usd ? ` at ${usd(best.cost_per_found_usd)}` : ""}`);
  parts.push(`${usd(s.month_spend_usd)} spent this month`);
  return parts.join(" · ");
}

/** The checklist rows IntegrationCard needs, from this card's own read (so it does not load the checklist again). */
export function vendorItems(s: ContactFinderStats): ChecklistItem[] {
  return KEYED.map((k) => ({ vendor_key: k, status: s.finders.find((f) => f.key === k)?.account_status ?? "not_started" }) as unknown as ChecklistItem);
}

// ------------------------------------------------------------------ the proposal
export interface Draft {
  /** Which finders are used (shown and saved in Ogma's order). */
  order: FinderKey[];
  phone_finding: boolean;
}

export function startDraft(s: ContactFinderStats): Draft {
  return { order: ogmaOrder(s.order), phone_finding: s.phone_finding };
}

/** The find_contact_finders change to propose: only the fields that changed, and the finders only when a finder was
 * switched on or off (never a new order). While the lawyer gate is shut, the gated finders are taken out and phones
 * stay off, so the change is never refused for them. */
export function proposal(s: ContactFinderStats, d: Draft): JsonObject {
  const order = ogmaOrder(s.provider_data_allowed ? d.order : d.order.filter((k) => !GATED.includes(k)));
  const out: JsonObject = {};
  if (JSON.stringify(order) !== JSON.stringify(ogmaOrder(s.order))) out.email_finding = order;
  const phone = s.provider_data_allowed ? d.phone_finding : false;
  if (phone !== s.phone_finding) out.phone_finding = phone;
  return out;
}

/** "Version 7 is waiting for your approval" lines and the like, for the save button's result. */
export function savedLine(version: number): string {
  return `Saved as version ${version}. Approve it to start using it.`;
}
